import { Layout, Menu, Button, Select, Tooltip, Typography } from "antd";
import { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { routes } from "@/config/routes";
import { useAuth } from "@/context/AuthContext";
import { useMutation, useQuery } from "@tanstack/react-query";
import { getSwitchableAccounts, switchAccount } from "@/services/accountSwitch.service";
import { applySwitchedSession } from "@/utils/accountSwitching";
import { isPublicTokenPath } from "@/utils/api";
import { ecGetOrders } from "@/pages/ec/services";
import { currencyBadge, soleRecordCurrency } from "@/utils/currencyBadge";
import { toast } from "react-toastify";

const { Header, Sider, Content } = Layout;
const { Text } = Typography;

const EndCustomerLayout = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { auth, logout } = useAuth();

  /**
   * Fetched rather than read off `auth.user.availableAccounts`: that field is only
   * populated by the EC login/switch responses and is wiped by the very next
   * `GET /users/me` refresh (which does not return it), so the switcher used to
   * disappear moments after login and stay gone across reloads.
   *
   * Suppressed on public token pages (proposal / work-order / pay links) for the same
   * reason AuthContext suppresses `GET /users/me` there: the recipient may be carrying
   * a stale session from another account, and any authenticated background request
   * fired from those pages 401s. See the note on `isPublicTokenPath` in utils/api.
   */
  const { data: switchableAccounts } = useQuery({
    queryKey: ["switchable-accounts"],
    queryFn: getSwitchableAccounts,
    enabled: !!auth.user && !isPublicTokenPath(location.pathname),
    placeholderData: (prev) => prev,
  });

  /**
   * The target may be a channel-partner account, so the landing route is derived from
   * the account being entered — /ec/projects is not mounted for a partner session.
   * The full page load also drops this account's react-query cache, which the
   * previous `authUser` + `navigate` switch left live.
   */
  const { mutate: requestAccountSwitch, isPending: isSwitchingAccount } = useMutation({
    mutationFn: (userId: string) => switchAccount(userId),
    onSuccess: (data) => {
      window.location.href = applySwitchedSession(localStorage, data);
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.message || error?.response?.data?.error || "Failed to switch account"
      );
    },
  });

  const menuItems = useMemo(
    () => [
      { key: routes.ec.projects, label: "Projects" },
      { key: routes.ec.orders, label: "Orders" },
      { key: routes.ec.paymentMethods, label: "Payment Methods" },
      { key: routes.ec.documents, label: "Documents" },
    ],
    []
  );

  /**
   * The customer's currency badge (2026-08-19), derived from the orders they
   * actually hold — NOT from `Account.currency`, which is `@default("USD")` and
   * is never written for an END_CUSTOMER, so it would print "Prices in USD" over
   * a CAD order. Same query key as the EC orders page, so this shares that cache
   * entry. Withheld when their orders disagree or there are none: every amount
   * in the portal already carries its own record's currency, so showing nothing
   * loses no information, while showing the wrong one misinforms.
   *
   * Gated exactly like the switchable-accounts query above — `isPublicTokenPath`,
   * not a hand-rolled prefix list — so a proposal/pay recipient carrying a stale
   * session fires no authenticated request for a badge they never see.
   */
  const { data: ecOrders } = useQuery({
    queryKey: ["ec", "orders"],
    queryFn: ecGetOrders,
    staleTime: 5 * 60 * 1000,
    enabled: !!auth.user && !isPublicTokenPath(location.pathname),
  });
  const badge = useMemo(
    () => currencyBadge(soleRecordCurrency(Array.isArray(ecOrders) ? ecOrders : [])),
    [ecOrders]
  );

  const availableAccounts = switchableAccounts || [];
  const selectedUserId = auth.user?.id;

  if (
    location.pathname.startsWith('/installer/') ||
    isPublicTokenPath(location.pathname)
  ) {
    return <>{children}</>;
  }

  return (
    <Layout style={{ minHeight: "100vh" }}>
      <Sider width={250} style={{ background: "#ffffff", borderRight: "1px solid #f0f0f0" }}>
        <div
          style={{
            color: "#111827",
            fontWeight: 600,
            fontSize: 16,
            lineHeight: 1.2,
            padding: "16px 20px",
            borderBottom: "1px solid #f0f0f0",
          }}
        >
          Swiftlane Projects
        </div>
        <div style={{ padding: "12px 20px", borderBottom: "1px solid #f0f0f0" }}>
          <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 6 }}>
            Account
          </Text>
          {availableAccounts.length > 1 ? (
            <Select
              size="small"
              value={selectedUserId}
              onChange={(value) => {
                if (value !== selectedUserId) {
                  requestAccountSwitch(value);
                }
              }}
              loading={isSwitchingAccount}
              style={{ width: "100%" }}
              // Keyed on userId, not accountId: a User row may have no account at
              // all, which would otherwise be unaddressable.
              options={availableAccounts.map((account) => ({
                label: account.accountName,
                value: account.userId,
              }))}
            />
          ) : (
            <Text strong>{auth.user?.accountName || "—"}</Text>
          )}
        </div>
        <Menu
          theme="light"
          mode="inline"
          style={{ borderInlineEnd: "none", paddingTop: 8 }}
          selectedKeys={[menuItems.find((item) => location.pathname.startsWith(item.key))?.key || ""]}
          items={menuItems}
          onClick={({ key }) => navigate(key)}
        />
      </Sider>
      <Layout>
        <Header style={{ background: "#fff", textAlign: "right", paddingInline: 24 }}>
          {badge && (
            <Tooltip title={badge.title}>
              <Typography.Text type="secondary" style={{ marginRight: 14, fontSize: 13, whiteSpace: "nowrap" }}>
                {badge.label}
              </Typography.Text>
            </Tooltip>
          )}
          <Button onClick={() => { logout(); navigate(routes.ec.login); }}>Logout</Button>
        </Header>
        <Content style={{ margin: 24 }}>{children}</Content>
      </Layout>
    </Layout>
  );
};

export default EndCustomerLayout;
